import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Badge,
  Button,
  Spinner,
} from 'reactstrap';
import { ToastContainer } from 'react-toastify';
import DataTable from 'react-data-table-component';
import useImprovementClarificationList from '../hooks/useImprovementClarificationList';
import useIcListProcurementMitraMap from '../hooks/useIcListProcurementMitraMap';
import {
  IMPROVEMENT_STATUS_META,
  CLARIFICATION_STATUS_META,
} from '../utils/improvementClarificationHelpers';
import { CLARIFICATION_PROGRESS } from '../../../shared/offer-domain/clarification';
import { useClarificationUnreadByProcurement } from '../../../shared/offer-domain/respond';
const isWaitingResponseFromDmut = row => {
  const key = Number(row?.statusClarificationProgress);
  if (key === CLARIFICATION_PROGRESS.WAITING_DMUT) return true;
  const label = String(row?.statusClarification ?? '')
    .trim()
    .toLowerCase();
  return (
    label === 'waiting response from dmut' ||
    label === 'waiting respond from dmut'
  );
};

const StatusPill = ({ wording, metaMap, fallbackColor = 'secondary' }) => {
  if (!wording) return <span className='text-muted'>-</span>;

  const meta = metaMap[wording] || {};
  const color = meta.color || fallbackColor;

  return (
    <Badge
      color={color}
      className='px-2 py-1 fs-12 fw-medium rounded-pill text-nowrap'
      title={wording}
    >
      {wording}
    </Badge>
  );
};

const ImprovementClarificationTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
}) => {
  const navigate = useNavigate();

  const {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    statusImprovementFilter,
    statusClarificationFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusImprovementFilter,
    handleStatusClarificationFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useImprovementClarificationList({
    procurementTypeHeader,
    refreshTrigger,
  });

  const procurementIds = useMemo(
    () => (data?.items ?? []).map(item => item.id).filter(Boolean),
    [data?.items]
  );
  const seedMitraByProcurementId = useMemo(() => {
    const map = {};
    (data?.items ?? []).forEach(item => {
      if (item?.id && item?.procurementMitraId) {
        map[item.id] = item.procurementMitraId;
      }
    });
    return map;
  }, [data?.items]);
  const procurementMitraByProcurementId = useIcListProcurementMitraMap({
    procurementIds,
    seedMitraByProcurementId,
    enabled: procurementIds.length > 0,
  }).resolvedMap;
  const { unreadByProcurementId } = useClarificationUnreadByProcurement({
    procurementIds,
    procurementMitraByProcurementId,
    isEvaluatorView: false,
    enabled: procurementIds.length > 0,
    refreshKey: refreshTrigger,
  });

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const MobileCard = ({ item }) => {
    const hasUnread = Boolean(unreadByProcurementId[String(item.id)]);
    return (
      <div
        className={`card mb-3 d-md-none border shadow-none${hasUnread ? ' border-info' : ''}`}
      >
        <div className={`card-body p-3${hasUnread ? ' bg-soft-info' : ''}`}>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <span
              className={
                hasUnread
                  ? 'text-info fw-semibold fs-13'
                  : 'text-primary fw-semibold fs-13'
              }
            >
              {item.idProc}
            </span>
          </div>

          <h6 className='card-title fw-bold mb-1 text-dark lh-base'>
            {item.title}
          </h6>
          <p className='text-muted fs-12 mb-2'>
            {item.productType?.product?.name || item.productType?.name || '-'}{' '}
            <span className='mx-1'>•</span> {item.productType?.name || '-'}
          </p>

          <div className='d-flex flex-wrap gap-3 mb-3 pb-3'>
            <div>
              <span className='text-muted fs-11 text-uppercase d-block mb-1'>
                Proc Type
              </span>
              <span className='fs-12 fw-medium text-dark'>{item.procType}</span>
            </div>
            <div>
              <span className='text-muted fs-11 text-uppercase d-block mb-1'>
                Status Improvement
              </span>
              <StatusPill
                wording={item.statusImprovement}
                metaMap={IMPROVEMENT_STATUS_META}
              />
            </div>
            <div>
              <span className='text-muted fs-11 text-uppercase d-block mb-1'>
                Status Clarification
              </span>
              <StatusPill
                wording={item.statusClarification}
                metaMap={CLARIFICATION_STATUS_META}
              />
            </div>
          </div>

          <Button
            color='light'
            size='sm'
            className='w-100 border bg-white shadow-none text-dark fw-medium py-2'
            onClick={() =>
              navigate(`/apps-improvementclarification/dtl/${item.id}`, {
                state: {
                  statusImprovement: item.statusImprovement,
                  statusClarification: item.statusClarification,
                  statusClarificationProgress: item.statusClarificationProgress,
                  procurementMitraId: item.procurementMitraId,
                  idOffer: item.idOffer,
                },
              })
            }
          >
            <i className='ri-eye-line align-bottom me-1 text-muted'></i> View
            Detail
          </Button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      id: 'idProc',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>ID Proc</span>
      ),
      selector: row => row.idProc,
      cell: row => {
        const hasUnread = Boolean(unreadByProcurementId[String(row.id)]);
        return (
          <span className={hasUnread ? 'text-info fw-semibold' : 'text-dark'}>
            {row.idProc}
          </span>
        );
      },
      width: '130px',
      sortable: true,
    },
    {
      id: 'title',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Title</span>
      ),
      selector: row => row.title,
      cell: row => (
        <span className='text-dark text-truncate' title={row.title}>
          {row.title}
        </span>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      id: 'procType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Proc Type
        </span>
      ),
      selector: row => row.procType,
      cell: row => <span className='text-dark'>{row.procType}</span>,
      width: '160px',
      sortable: true,
    },
    {
      id: 'productType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Product Type
        </span>
      ),
      selector: row => row.productType?.product?.name || row.productType?.name,
      cell: row => (
        <div className='d-flex flex-column justify-content-center py-2'>
          <span className='fw-medium text-dark'>
            {row.productType?.product?.name || row.productType?.name || '-'}
          </span>
          <span className='text-muted small' style={{ fontSize: '11px' }}>
            {row.productType?.name || '-'}
          </span>
        </div>
      ),
      minWidth: '160px',
      sortable: true,
    },
    {
      id: 'description',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Description
        </span>
      ),
      selector: row => row.description,
      cell: row => (
        <span className='text-dark text-truncate' title={row.description}>
          {row.description || '-'}
        </span>
      ),
      minWidth: '180px',
      sortable: true,
    },
    {
      id: 'statusImprovement',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Status Improvement
        </span>
      ),
      selector: row => row.statusImprovement,
      cell: row => (
        <StatusPill
          wording={row.statusImprovement}
          metaMap={IMPROVEMENT_STATUS_META}
        />
      ),
      minWidth: '180px',
      sortable: true,
      wrap: false,
    },
    {
      id: 'statusClarification',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Status Clarification
        </span>
      ),
      selector: row => row.statusClarification,
      cell: row => (
        <StatusPill
          wording={row.statusClarification}
          metaMap={CLARIFICATION_STATUS_META}
        />
      ),
      minWidth: '280px',
      sortable: true,
      wrap: false,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          <button
            className='btn btn-soft-primary btn-sm btn-icon'
            onClick={() =>
              navigate(`/apps-improvementclarification/dtl/${row.id}`, {
                state: {
                  statusImprovement: row.statusImprovement,
                  statusClarification: row.statusClarification,
                  statusClarificationProgress: row.statusClarificationProgress,
                  procurementMitraId: row.procurementMitraId,
                  idOffer: row.idOffer,
                },
              })
            }
            title='Detail'
          >
            <i className='ri-eye-line align-bottom'></i>
          </button>
        </div>
      ),
      width: '90px',
      center: true,
      sortable: false,
    },
  ];

  return (
    <>
      <ToastContainer closeButton={false} />
      <CardBody className='pt-0'>
        <Row className='mb-3'>
          <Col xs={12}>
            <Row className='g-3'>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Search</Label>
                <Input
                  type='text'
                  placeholder='Search by title or procurement no...'
                  value={searchText}
                  onChange={handleSearch}
                  className='form-control'
                />
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Status Improvement</Label>
                <Input
                  type='select'
                  value={statusImprovementFilter}
                  onChange={e => handleStatusImprovementFilter(e.target.value)}
                  className='form-control'
                >
                  <option value=''>All Status</option>
                  <option value='Open'>Open</option>
                  <option value='Closing'>Closing</option>
                  <option value='Submitted'>Submitted</option>
                </Input>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Status Clarification</Label>
                <Input
                  type='select'
                  value={statusClarificationFilter}
                  onChange={e =>
                    handleStatusClarificationFilter(e.target.value)
                  }
                  className='form-control'
                >
                  <option value=''>All Status</option>
                  <option value='Open'>Open</option>
                  <option value='Waiting Response from Dmut'>
                    Waiting Response from Dmut
                  </option>
                  <option value='Waiting Response from Panitia Tender'>
                    Waiting Response from Panitia Tender
                  </option>
                  <option value='Closing'>Closing</option>
                </Input>
              </Col>
              <Col xs={12} md={6} lg={3} className='d-flex align-items-end'>
                <Button
                  color='danger'
                  outline
                  onClick={clearFilters}
                  className='w-100'
                >
                  <i className='ri-refresh-line me-1'></i>
                  <span className='d-none d-sm-inline'>Clear Filters</span>
                  <span className='d-sm-none'>Clear</span>
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className='mb-2'>
          <Col xs={12}>
            <small className='text-muted'>
              {isLoading
                ? 'Loading...'
                : `Showing ${startEntry} to ${endEntry} of ${totalRows} entries`}
            </small>
          </Col>
        </Row>

        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner color='primary' />
            <p className='text-muted small mt-2 mb-0'>
              Loading improvement clarification data...
            </p>
          </div>
        ) : (
          <>
            <div className='d-md-none mt-3'>
              {data.items?.length > 0 ? (
                data.items.map((item, index) => (
                  <MobileCard key={item.id || index} item={item} />
                ))
              ) : (
                <div className='table-empty text-center py-4'>
                  <i
                    className='ri-inbox-line text-muted'
                    style={{ fontSize: '3rem', opacity: 0.5 }}
                  ></i>
                  <p className='mt-2 text-muted'>No data found</p>
                </div>
              )}
            </div>

            <div className='d-none d-md-block'>
              <div
                className='table-responsive'
                style={{ maxHeight: '600px', overflowY: 'auto' }}
              >
                <DataTable
                  columns={columns}
                  data={data.items}
                  pagination={false}
                  onSort={handleSort}
                  sortServer
                  defaultSortFieldId='idProc'
                  defaultSortAsc={false}
                  highlightOnHover
                  responsive
                  striped
                  conditionalRowStyles={[
                    {
                      when: isWaitingResponseFromDmut,
                      classNames: ['table-warning'],
                      style: {
                        backgroundColor: 'var(--vz-warning-bg-subtle, #fff3cd)',
                      },
                    },
                    {
                      when: row =>
                        !isWaitingResponseFromDmut(row) &&
                        Boolean(unreadByProcurementId[String(row.id)]),
                      classNames: ['table-info'],
                      style: {
                        backgroundColor: 'var(--vz-info-bg-subtle, #cff4fc)',
                      },
                    },
                  ]}
                  persistTableHead
                  dense
                  customStyles={{
                    header: {
                      style: {
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        fontWeight: 'bold',
                        minHeight: '40px',
                      },
                    },
                    headCells: {
                      style: {
                        backgroundColor: '#f0f0f0',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                      },
                    },
                    cells: {
                      style: {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        wordBreak: 'break-word',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                      },
                    },
                    rows: { style: { minHeight: '45px' } },
                    table: { style: { width: '100%' } },
                    tableWrapper: { style: { display: 'table' } },
                  }}
                  noDataComponent={
                    <div className='table-empty text-center py-4'>
                      <i
                        className='ri-inbox-line text-muted'
                        style={{ fontSize: '3rem', opacity: 0.5 }}
                      ></i>
                      <p className='mt-2 text-muted'>No data found</p>
                    </div>
                  }
                />
              </div>
            </div>
          </>
        )}

        <div className='d-flex justify-content-between align-items-center mt-3 px-3 py-2 border-top'>
          <div className='d-flex align-items-center'>
            <span className='text-muted me-2'>Rows per page:</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className='d-flex align-items-center'>
            <span className='text-muted me-3'>
              {startEntry}-{endEntry} of {totalRows}
            </span>
            <div className='btn-group' role='group'>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage <= 1 || isLoading}
                onClick={() => handlePageChange(1)}
              >
                <i className='ri-skip-back-line'></i>
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage <= 1 || isLoading}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className='ri-arrow-left-s-line'></i>
              </button>
              <span className='btn btn-outline-secondary btn-sm disabled'>
                {currentPage}
              </span>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className='ri-arrow-right-s-line'></i>
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(totalPages)}
              >
                <i className='ri-skip-forward-line'></i>
              </button>
            </div>
          </div>
        </div>
      </CardBody>
    </>
  );
};

export default React.memo(ImprovementClarificationTable);
